"use client";

import { useState, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import Image from "next/image";
import { Hub } from "aws-amplify";
import {
	User,
	LogOut,
	Mail,
	Plus,
	Clock,
	LogIn,
	ArrowLeft,
	CreditCard,
	ArrowRight,
} from "lucide-react";
import Link from "next/link";
import LogoIcon from "@/assets/images/logo.svg";
import { signOutUser, getCognitoUserEmail } from "@/services/cognito";
import { useImageryRequestsHeaderSlot } from "@/contexts/ImageryRequestsHeaderContext";
import styles from "@/components/dashboard/DashboardHeader.module.css";
import {
	enrichmentChromeMaxWidth,
	enrichmentChromeViewportInset,
	enrichmentDarkPanelBase,
	enrichmentDarkSlimPadding,
} from "@/components/enrichment/enrichmentChromeClasses";
import { BRAND_NAME } from "@/lib/branding";
import { isAuthenticated } from "@/lib/auth";
import { clearImageryEmailCookie } from "@/lib/cookies";

function normalizedPathname(pathname: string | null): string {
	if (!pathname) return "";
	return pathname.length > 1 && pathname.endsWith("/")
		? pathname.slice(0, -1)
		: pathname;
}

/** `/imagery/requests/:requestId` (single segment), excluding `/imagery/requests/new`. */
function isImageryRequestDetailPath(pathname: string | null): boolean {
	const p = normalizedPathname(pathname);
	if (!p.startsWith("/imagery/requests/")) return false;
	const rest = p.slice("/imagery/requests/".length);
	if (!rest || rest.includes("/")) return false;
	if (rest === "new") return false;
	return true;
}

/** Request detail: icon-only circular back (black, white arrow). */
const imageryDetailBackIconButtonClass =
	"relative z-[2] flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-transparent bg-black text-white shadow-md transition-[box-shadow,background-color,transform,border-color] hover:border-[#A7FF5E] hover:bg-neutral-900 hover:shadow-lg active:scale-[0.97] focus:outline-none focus-visible:border-[#A7FF5E] focus-visible:ring-2 focus-visible:ring-[#A7FF5E]/40";

/** Match Request Photos modal primary CTA (Continue): lime pill, subtle border, soft elevation */
const imageryHeaderCtaBase =
	"inline-flex min-h-9 min-w-[6.25rem] w-fit shrink-0 items-center justify-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-semibold tracking-tight shadow-lg shadow-[#16251c]/25 transition-all duration-200 sm:min-w-[7rem] sm:px-3 sm:text-[13px] active:scale-[0.98]";

/**
 * Imagery shell header: logo, optional email strip, actions.
 * Guests (not Cognito): "Check in" or "My requests" → /imagery/requests depending on imagery session.
 * Signed-in: My requests (when not already on list/detail) + Profile + Sign out (+ same imagery CTAs as guests when session slot is set).
 */
export function ImageryUnifiedShellHeader() {
	const router = useRouter();
	const pathname = usePathname();
	const normalizedPath = normalizedPathname(pathname);
	const [isSigningOut, setIsSigningOut] = useState(false);
	const [isCognitoAuthed, setIsCognitoAuthed] = useState(false);
	const [cognitoEmail, setCognitoEmail] = useState<string | null>(null);
	const { slot, setImageryRequestsHeaderSlot } =
		useImageryRequestsHeaderSlot();
	const onImageryRequestsPage = normalizedPath === "/imagery/requests";
	const onImageryRequestDetail = isImageryRequestDetailPath(pathname);
	const onImageryNewRequestPage =
		normalizedPath === "/imagery/request/new" ||
		normalizedPath === "/imagery/requests/new";

	useEffect(() => {
		let cancelled = false;
		void isAuthenticated().then((v) => {
			if (!cancelled) setIsCognitoAuthed(v);
		});
		const listener = (data: { payload: { event: string } }) => {
			const e = data.payload.event;
			if (e === "signIn" || e === "cognitoHostedUI") {
				setIsCognitoAuthed(true);
			} else if (e === "signOut") {
				setIsCognitoAuthed(false);
			}
		};
		Hub.listen("auth", listener);
		return () => {
			cancelled = true;
			Hub.remove("auth", listener);
		};
	}, []);

	useEffect(() => {
		if (!isCognitoAuthed) {
			setCognitoEmail(null);
			return;
		}
		let cancelled = false;
		void getCognitoUserEmail().then((e) => {
			if (!cancelled) setCognitoEmail(e);
		});
		return () => {
			cancelled = true;
		};
	}, [isCognitoAuthed]);

	const handleSignOut = async () => {
		try {
			setIsSigningOut(true);
			await signOutUser();
			router.push("/login");
		} catch (error) {
			console.error("Failed to sign out:", error);
			setIsSigningOut(false);
		}
	};

	const handleImageryCheckout = () => {
		clearImageryEmailCookie();
		setImageryRequestsHeaderSlot(null);
		router.push("/imagery");
	};

	return (
		<header
			className={`fixed inset-x-0 top-0 z-[10000] ${enrichmentChromeViewportInset}`}
		>
			<div className={enrichmentChromeMaxWidth}>
				<div
					className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-[#edffde] ${enrichmentDarkPanelBase} ${enrichmentDarkSlimPadding}`}
				>
					<div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:gap-3">
						{onImageryRequestDetail ? (
							<button
								type="button"
								onClick={() => router.push("/imagery/requests")}
								className={imageryDetailBackIconButtonClass}
								aria-label="Back to requests"
								title="Back to requests"
							>
								<ArrowLeft
									className="h-4 w-4 shrink-0"
									strokeWidth={2.25}
									aria-hidden
								/>
							</button>
						) : null}

						<Link
							href="/"
							className={`relative z-0 ${styles.logoContainer}`}
						>
							<Image
								src={LogoIcon}
								alt={`${BRAND_NAME} Logo`}
								width={32}
								height={32}
								className={styles.logoImage}
								priority
							/>
							<span className={styles.logoText}>
								{BRAND_NAME}
							</span>
						</Link>

						{slot ? (
							<div className="flex min-w-0 flex-1 basis-[min(100%,14rem)] items-center gap-2 sm:gap-2.5">
								<Mail
									className="h-3.5 w-3.5 shrink-0 text-[#edffde]/55"
									aria-hidden
								/>
								<span
									className="min-w-0 truncate text-[11px] leading-tight text-[#edffde]/80 sm:text-xs"
									title={slot.email}
								>
									{slot.email}
								</span>
								<button
									type="button"
									onClick={slot.onDifferentEmail}
									className="shrink-0 whitespace-nowrap text-[10px] font-semibold text-[#A7FF5E] underline-offset-2 hover:underline sm:text-[11px]"
								>
									Change email
								</button>
							</div>
						) : isCognitoAuthed && cognitoEmail ? (
							<div className="flex min-w-0 flex-1 basis-[min(100%,14rem)] items-center gap-2 sm:gap-2.5">
								<Mail
									className="h-3.5 w-3.5 shrink-0 text-[#edffde]/55"
									aria-hidden
								/>
								<span
									className="min-w-0 truncate text-[11px] leading-tight text-[#edffde]/80 sm:text-xs"
									title={cognitoEmail}
								>
									{cognitoEmail}
								</span>
								<Link
									href="/profile"
									className="shrink-0 whitespace-nowrap text-[10px] font-semibold text-[#A7FF5E] underline-offset-2 hover:underline sm:text-[11px]"
								>
									Account
								</Link>
							</div>
						) : null}
					</div>

					<div className={`${styles.rightSection} shrink-0`}>
						{slot && !onImageryNewRequestPage ? (
							<button
								type="button"
								onClick={slot.onNewRequest}
								aria-label="Start a new imagery request"
								className={`${imageryHeaderCtaBase} group border border-[#12301b]/15 bg-[#A7FF5E] text-[#16251c] hover:bg-[#cbf7a6] hover:shadow-xl hover:shadow-[#16251c]/28`}
							>
								<Plus
									className="h-3.5 w-3.5 shrink-0"
									aria-hidden
								/>
								<span>
									New
									<span className="hidden sm:inline">
										{" "}
										request
									</span>
								</span>
								<ArrowRight
									className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5"
									aria-hidden
								/>
							</button>
						) : null}
						{!onImageryRequestsPage && !onImageryRequestDetail ? (
							<Link
								href="/imagery/requests"
								className={`${imageryHeaderCtaBase} border-2 border-[#A7FF5E] bg-transparent text-[#A7FF5E] no-underline hover:border-[#cbf7a6] hover:bg-[#A7FF5E]/12 hover:text-[#cbf7a6] hover:shadow-xl hover:shadow-[#16251c]/30`}
								aria-label={
									isCognitoAuthed || slot
										? "My photo requests"
										: "Check in with your email"
								}
							>
								<span
									className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black/30 ring-1 ring-[#A7FF5E]/45"
									aria-hidden
								>
									{isCognitoAuthed || slot ? (
										<Clock className="h-3.5 w-3.5 text-[#A7FF5E]" />
									) : (
										<LogIn className="h-3.5 w-3.5 text-[#A7FF5E]" />
									)}
								</span>
								<span className="whitespace-nowrap">
									{isCognitoAuthed || slot
										? "My requests"
										: "Check in"}
								</span>
							</Link>
						) : null}
						{slot ? (
							<button
								type="button"
								onClick={handleImageryCheckout}
								className={`${imageryHeaderCtaBase} border border-white/30 bg-neutral-800/95 text-[#edffde] backdrop-blur-[2px] hover:border-white/45 hover:bg-neutral-700 hover:shadow-xl hover:shadow-black/50`}
							>
								<CreditCard
									className="h-3.5 w-3.5 shrink-0 opacity-95"
									aria-hidden
								/>
								Checkout
							</button>
						) : null}
						{isCognitoAuthed ? (
							<>
								<Link
									href="/profile"
									data-testid="profile-link"
									className={styles.profileContainer}
								>
									<div className={styles.profileAvatar}>
										<User
											className={styles.profileIconSvg}
										/>
									</div>
									<span className={styles.profileText}>
										Profile
									</span>
								</Link>
								<button
									type="button"
									onClick={handleSignOut}
									disabled={isSigningOut}
									className={styles.signOutContainer}
									data-testid="sign-out-button"
								>
									<div className={styles.signOutAvatar}>
										<LogOut
											className={styles.signOutIconSvg}
										/>
									</div>
									<span className={styles.signOutText}>
										Sign Out
									</span>
								</button>
							</>
						) : null}
					</div>
				</div>
			</div>
		</header>
	);
}
